import React, { useMemo } from 'react';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import DiscoveryCapabilityCard from './DiscoveryCapabilityCard';
import DiscoveryKnowledgeCard from './DiscoveryKnowledgeCard';
import {
  filterVisibleDiscoveryResults,
  filterVisibleKnowledgeResults,
  getRecommendationTierLabel,
  pickPrimaryDiscoveryResult,
  normalizeRelevanceLevel,
} from './discoveryRelevanceStyles';
import { colors } from '../../../designTokens';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../assistantButtonStyles';

export default function DiscoveryResultsList({
  kind,
  results = [],
  onDiscoveryAction,
  isBusy = false,
  workspacePromptIds = [],
  assistantContext = 'standard',
  builderCapabilityAdded = false,
  query = '',
}) {
  const isContext = kind === 'contexts';
  const visibleResults = useMemo(
    () => isContext ? filterVisibleKnowledgeResults(results) : filterVisibleDiscoveryResults(results),
    [results, isContext]
  );
  const workspacePromptIdSet = useMemo(
    () => new Set((workspacePromptIds || []).map((id) => String(id))),
    [workspacePromptIds]
  );
  const hasWorkspaceCapability = workspacePromptIdSet.size > 0;
  const canUseDiscoveryCapability = assistantContext === 'builder'
    ? builderCapabilityAdded
    : hasWorkspaceCapability;
  const showInlineActions = assistantContext === 'builder' || !canUseDiscoveryCapability;
  const primaryResult = useMemo(
    () => pickPrimaryDiscoveryResult(visibleResults),
    [visibleResults]
  );
  const otherResultGroups = useMemo(() => {
    const rest = visibleResults.slice(1);
    const groups = ['Strong Matches', 'Relevant Matches']
      .map((label) => ({
        label,
        results: rest.filter((result) => getRecommendationTierLabel(result.relevance) === label),
      }))
      .filter((group) => group.results.length > 0);
    return groups;
  }, [visibleResults]);

  const handleGlobalAction = (action) => {
    if (!primaryResult) return;
    const item = primaryResult.prompt || primaryResult.context || primaryResult.raw || primaryResult;
    onDiscoveryAction?.({ action, item, result: primaryResult, kind, query: query || primaryResult.query || '' });
  };

  return (
    <Stack spacing={1.75} sx={{ mt: 1.5 }}>
      {primaryResult && (
        <Stack spacing={0.85}>
          <Typography
            variant="caption"
            sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8125rem', letterSpacing: 0 }}
          >
            {isContext ? 'Strong knowledge match' : normalizeRelevanceLevel(primaryResult.relevance) >= 4 ? 'Best Match' : 'Related capability'}
          </Typography>
          {isContext ? (
            <DiscoveryKnowledgeCard
              result={primaryResult}
              kind={kind}
              onDiscoveryAction={onDiscoveryAction}
              isBusy={isBusy}
              assistantContext={assistantContext}
              featured
            />
          ) : (
            <DiscoveryCapabilityCard
              result={primaryResult}
              kind={kind}
              onDiscoveryAction={onDiscoveryAction}
              isBusy={isBusy}
              isInWorkspace={workspacePromptIdSet.has(String((primaryResult.prompt || primaryResult.raw || primaryResult)?.id))}
              assistantContext={assistantContext}
              featured
            />
          )}
        </Stack>
      )}
      {otherResultGroups.length > 0 && (
        <Stack spacing={1.35}>
          <Divider sx={{ borderColor: 'rgba(15, 23, 42, 0.08)' }} />
          {otherResultGroups.map((group) => (
            <Stack key={group.label} spacing={0.85}>
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary, fontWeight: 600, fontSize: '0.8125rem', letterSpacing: 0 }}
              >
                {group.label} ({group.results.length})
              </Typography>
              <Stack spacing={0.9}>
                {group.results.map((result) => {
                  const item = result.prompt || result.context || result.raw || result;
                  const key = result.id || item?.id || `${kind}-${result.name || result.title}`;
                  return isContext ? (
                    <DiscoveryKnowledgeCard
                      key={key}
                      result={result}
                      kind={kind}
                      onDiscoveryAction={onDiscoveryAction}
                      isBusy={isBusy}
                      assistantContext={assistantContext}
                    />
                  ) : (
                    <DiscoveryCapabilityCard
                      key={key}
                      result={result}
                      kind={kind}
                      onDiscoveryAction={onDiscoveryAction}
                      isBusy={isBusy}
                      isInWorkspace={workspacePromptIdSet.has(String(item?.id))}
                      assistantContext={assistantContext}
                    />
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      {!isContext && visibleResults.length > 0 && showInlineActions && (
        <>
          <Divider sx={{ my: 0.25, borderColor: 'rgba(15, 23, 42, 0.08)' }} />
          <Box
            sx={{
              display: 'flex',
              gap: 0.65,
              flexWrap: 'wrap',
              pt: 0.25,
              alignItems: 'center',
            }}
          >
            {!canUseDiscoveryCapability && assistantContext !== 'builder' && (
              <Typography
                sx={{
                  width: '100%',
                  color: colors.textSecondary,
                  fontSize: '0.8125rem',
                  lineHeight: 1.45,
                  mb: 0.3,
                }}
              >
                Choose a capability to add to your workspace, or refine a match first. Then you can add knowledge or run it.
              </Typography>
            )}
            <Button
              size="small"
              variant="contained"
              disabled={isBusy || !canUseDiscoveryCapability}
              onClick={() => handleGlobalAction('find-knowledge')}
              sx={assistantPrimaryButtonSx}
            >
              Find knowledge
            </Button>
            {assistantContext === 'builder' && builderCapabilityAdded ? (
              <Button
                size="small"
                variant="outlined"
                disabled={isBusy}
                onClick={() => handleGlobalAction('complete-builder-session')}
                sx={assistantSecondaryButtonSx}
              >
                Done
              </Button>
            ) : (
              <>
                {assistantContext !== 'builder' && (
                  <Button size="small" variant="outlined" disabled={isBusy || !canUseDiscoveryCapability} onClick={() => handleGlobalAction('run-capability')} sx={assistantSecondaryButtonSx}>
                    Run
                  </Button>
                )}
                <Button size="small" variant="text" disabled={isBusy} onClick={() => handleGlobalAction('build-from-discovery')} sx={assistantTextButtonSx}>
                  Build
                </Button>
                <Button size="small" variant="text" disabled={isBusy} onClick={() => handleGlobalAction('chat-from-discovery')} sx={assistantTextButtonSx}>
                  Chat
                </Button>
              </>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
